import type { Ref } from 'vue'

import type {
  RaidGroup,
  RaidId,
  RaidPlayer,
  RaidRoom,
  RaidStage,
  RaidStageDiff,
  RaidStageId,
  RaidBoardPayload,
} from '../../types'

/**
 * Read-only ctx the raid-lifecycle composable needs. Mirrors the slice
 * of `RaidActionsCtx` that `selectRaid` actually touches — the lifecycle
 * owns the snapshot-selection race guard + rollback but does NOT issue
 * any mutations. Splitting the interface keeps every composable that
 * composes this one honest about what it depends on.
 *
 * `run` is included in the type signature for symmetry with the other
 * raid sub-modules even though `selectRaid` does not currently use it —
 * `applyRaidToBoard`/`deleteRaid` (which still live in `actions.ts`)
 * delegate back into `selectRaid` via a `selectRaid` ctx slot rather
 * than reading a shared module-local counter directly.
 */
export interface RaidLifecycleCtx {
  readonly activeRaidId: Ref<RaidId | null>
  readonly activeRaidBoard: Ref<RaidBoardPayload | null>
  readonly raidRooms: Ref<ReadonlyArray<RaidRoom>>
  readonly raidGroups: Ref<ReadonlyArray<RaidGroup>>
  readonly raidPlayers: Ref<ReadonlyArray<RaidPlayer>>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly raidStages: Ref<ReadonlyArray<RaidStage>>
  readonly raidStageDiffs: Ref<ReadonlyArray<RaidStageDiff>>
  readonly stageSelectionPending: Ref<boolean>
  readonly loadStagesForRaid: (raidId: RaidId, shouldApply?: () => boolean) => Promise<boolean>
  readonly fetchRaidBoard: (raidId: RaidId, shouldApply?: () => boolean) => Promise<boolean>
}

export interface RaidLifecycleApi {
  /**
   * Drop every raid-shaped ref back to its empty state and return
   * `true`. The landing surface for the Calendar's `+ Plan` button,
   * the raid selector's `Live board` button, and the post-apply
   * clearing in `applyRaidToBoard` / `deleteRaid`.
   */
  readonly selectRaid: (raidId: RaidId | null) => Promise<boolean>
}

/**
 * Owns the `selectRaid` action — the action is the only mutation in the
 * raid surface that has a race-guard (the `latestSelection` counter
 * below). It is also the only action that touches seven refs at once
 * and rolls them back when the snapshot fetch fails. Extracting it from
 * `actions.ts` keeps the rest of the surface (create / schedule /
 * cancel / delete / apply) grouped by intent, and
 * keeps the rollback snapshot + counter module-local where they belong.
 *
 * Module-local state stays module-local on purpose: do NOT hoist
 * `latestSelection` into the ctx — every consumer wires its own ctx
 * and the counter must be a singleton bound to the singleton Pinia
 * store, which is exactly what a top-level `let` inside the factory
 * closure provides.
 */
export function useRaidLifecycleActions(ctx: RaidLifecycleCtx): RaidLifecycleApi {
  let latestSelection = 0

  async function selectRaid(raidId: RaidId | null): Promise<boolean> {
    const selection = ++latestSelection
    if (raidId === null) {
      ctx.activeRaidId.value = null
      ctx.activeRaidBoard.value = null
      ctx.raidRooms.value = []
      ctx.raidGroups.value = []
      ctx.raidPlayers.value = []
      ctx.activeStageId.value = null
      ctx.raidStages.value = []
      ctx.raidStageDiffs.value = []
      ctx.stageSelectionPending.value = false
      return true
    }
    const isCurrentSelection = (): boolean => selection === latestSelection
    const previous = {
      activeRaidId: ctx.activeRaidId.value,
      activeRaidBoard: ctx.activeRaidBoard.value,
      raidRooms: ctx.raidRooms.value,
      raidGroups: ctx.raidGroups.value,
      raidPlayers: ctx.raidPlayers.value,
      activeStageId: ctx.activeStageId.value,
      raidStages: ctx.raidStages.value,
      raidStageDiffs: ctx.raidStageDiffs.value,
      stageSelectionPending: ctx.stageSelectionPending.value,
    }
    ctx.activeRaidId.value = raidId
    ctx.activeStageId.value = null
    ctx.raidStages.value = []
    ctx.raidStageDiffs.value = []
    // The empty stage array is only a transient reset until the selected
    // raid's catalog query (or a newer subscription snapshot) establishes
    // the authoritative catalog. Keep the toolbar busy so an officer cannot
    // briefly create a ninth/duplicate stage against this unknown state.
    ctx.stageSelectionPending.value = true
    const catalogLoaded = await ctx.loadStagesForRaid(raidId, isCurrentSelection)
    if (!isCurrentSelection()) return false
    // An empty list is authoritative only when the catalog query succeeded.
    // On failure, keep Add disabled until a matching subscription snapshot
    // hydrates the catalog and clears this pending flag.
    if (catalogLoaded) ctx.stageSelectionPending.value = false
    const loaded = await ctx.fetchRaidBoard(raidId, isCurrentSelection)
    if (!isCurrentSelection()) return false
    if (loaded) return true
    ctx.activeRaidId.value = previous.activeRaidId
    ctx.activeRaidBoard.value = previous.activeRaidBoard
    ctx.raidRooms.value = previous.raidRooms
    ctx.raidGroups.value = previous.raidGroups
    ctx.raidPlayers.value = previous.raidPlayers
    ctx.activeStageId.value = previous.activeStageId
    ctx.raidStages.value = previous.raidStages
    ctx.raidStageDiffs.value = previous.raidStageDiffs
    ctx.stageSelectionPending.value = previous.stageSelectionPending
    return false
  }

  return { selectRaid }
}
